import { NATIVE_UNIQ_IDENTIFIER, TEXT_UNIQ_IDENTIFIER } from '../consts';
import { isPureObject } from '../reactive';
import { Boxed } from '../reactive/boxed';
import { Text } from '../reactive/text';

/** Existing snapshot representation, traversed one native value at a time. */
export function* toJSONWork(value: unknown): Generator<void, unknown> {
  const hole = Symbol();
  type Frame = {
    entries: Iterator<[string | number, unknown]>;
    result: Record<string, unknown> | unknown[];
    parent?: Frame;
    key?: string | number;
  };
  const frames: Frame[] = [];
  const enter = (
    value: unknown,
    parent?: Frame,
    key?: string | number
  ): unknown => {
    if (value instanceof Boxed)
      return { [NATIVE_UNIQ_IDENTIFIER]: true, value: value.getValue() };
    if (value instanceof Text)
      return { [TEXT_UNIQ_IDENTIFIER]: true, delta: value.yText.toDelta() };
    if (Array.isArray(value)) {
      const length = value.length;
      const entries = function* (): Generator<[number, unknown]> {
        for (let index = 0; index < length; index++) {
          // Native Array.map preserves holes; visits still count as work.
          if (index in value) yield [index, value[index]];
          else yield [index, hole];
        }
      };
      frames.push({
        entries: entries(),
        result: new Array(length),
        parent,
        key,
      });
      return undefined;
    }
    if (isPureObject(value)) {
      const object = value as Record<string, unknown>;
      const entries = function* (): Generator<[string, unknown]> {
        for (const key in object)
          if (Object.hasOwn(object, key)) yield [key, object[key]];
      };
      frames.push({ entries: entries(), result: {}, parent, key });
      return undefined;
    }
    return value;
  };
  const assign = (frame: Frame, key: string | number, value: unknown) => {
    Object.defineProperty(frame.result, key, {
      value,
      enumerable: true,
      configurable: true,
      writable: true,
    });
  };
  yield;
  const terminal = enter(value);
  if (!frames.length) return terminal;
  while (frames.length) {
    const frame = frames[frames.length - 1];
    yield;
    const next = frame.entries.next();
    if (next.done) {
      frames.pop();
      if (!frame.parent) return frame.result;
      assign(frame.parent, frame.key!, frame.result);
      continue;
    }
    const [key, child] = next.value;
    if (child === hole) continue;
    const depth = frames.length;
    const result = enter(child, frame, key);
    if (frames.length === depth) assign(frame, key, result);
  }
  return undefined;
}

export function toJSON(value: unknown): unknown {
  const work = toJSONWork(value);
  let next = work.next();
  while (!next.done) next = work.next();
  return next.value;
}

export function fromJSON(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(fromJSON);
  }
  if (typeof value === 'object' && value != null) {
    if (Reflect.has(value, NATIVE_UNIQ_IDENTIFIER)) {
      return new Boxed(Reflect.get(value, 'value'));
    }
    if (Reflect.has(value, TEXT_UNIQ_IDENTIFIER)) {
      return new Text(Reflect.get(value, 'delta'));
    }
    return Object.fromEntries(
      Object.entries(value).map(([key, value]) => {
        return [key, fromJSON(value)];
      })
    );
  }

  return value;
}
